import { Button, Pagination } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import InputTextField from "src/components/atom/InputTextField"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"
import { useLoginContext } from "src/hooks/useLoginContext"
import { RESPONSE_CODE, SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import { validateField } from "src/modules/validation"
import { Memo, MemoService } from "src/openapi"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Box, Grid, Typography } from "src/UILibrary"
import * as yup from "yup"
import { MemoTableItem } from "../component/MemoTableItem"

type Props = {
  userID: string
}

const SORT_BY = {
  DATE: "updated_at",
  AUTHOR: "lastName",
  MEMO: "memo",
}

const PAGE_SIZE = 10

export const MemoList = ({ userID }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })
  const isPC = useMediaQuery({ query: "(min-width: 1400px)" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const { accessToken, checkLoginStatus, initialized } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [memos, setMemos] = useState<Memo[]>([])
  const [pageNum, setPageNum] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [currentSort, setCurrentSort] = useState<string>(SORT_BY.DATE)
  const [currentSortOrder, setCurrentSortOrder] = useState<string>(SORT_ORDER.DESCENDONG)

  const [memo, setMemo] = useState<string>("")
  const [memoValidationMessage, setMemoValidationMessage] = useState<string>("")
  const [selectedMemo, setSelectedMemo] = useState<Memo>()

  let headers: any[] = [
    {
      size: isPC ? 2 : isTablet ? 2 : 1,
      name: t("date"),
      sortby: SORT_BY.DATE,
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 1,
      name: t("memo_author"),
      sortby: SORT_BY.AUTHOR,
    },
    {
      size: isPC ? 5 : isTablet ? 5 : 1,
      name: t("memo_content"),
      sortby: SORT_BY.MEMO,
    },
    {
      size: isPC ? 3 : isTablet ? 3 : 1,
      name: "",
      sortby: "",
    },
  ]

  const getMemos = (sort: string, sortOrder: string) => {
    MemoService.listMemo(accessToken, userID, PAGE_SIZE, (pageNum - 1) * PAGE_SIZE, sortOrder, sort)
      .then((res) => {
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setMemos(res.data?.memos ? (res.data?.memos as Memo[]) : [])
          setTotal(res.data?.count as number)
        } else {
          setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const onSortChanged = (sorter: string, asc: boolean) => {
    getMemos(sorter, asc ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDONG)
    setCurrentSort(sorter)
    setCurrentSortOrder(asc ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDONG)
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    getMemos(currentSort, currentSortOrder)
  }, [pageNum, userID, initialized])

  useEffect(() => {
    if (selectedMemo) {
      setMemo(selectedMemo.memo as string)
    } else {
      setMemo("")
    }
  }, [selectedMemo])

  const validate = async () => {
    try {
      await validateField(
        yup.object().shape({
          memo: yup.string().required(t("please_input_memo")),
        }),
        "memo",
        memo
      )
      setMemoValidationMessage("")
    } catch (err: any) {
      setMemoValidationMessage(err.message)
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    if (selectedMemo) {
      MemoService.updateMemo(accessToken, selectedMemo.id as number, {
        memo: memo,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_memo_update"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            setSelectedMemo(undefined)
            getMemos(currentSort, currentSortOrder)
          } else {
            setShowToast({ show: true, text: t("failed_memo_update"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed_memo_update"), type: SNACKBAR_TYPES.ERROR })
        })
    } else {
      MemoService.createMemo(accessToken, {
        userId: userID,
        memo: memo,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_memo_create"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            setMemo("")
            getMemos(currentSort, currentSortOrder)
          } else {
            setShowToast({ show: true, text: t("failed_memo_create"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed_memo_create"), type: SNACKBAR_TYPES.ERROR })
        })
    }
  }

  const handleRemove = (item: Memo) => {
    setMemo("")
    setSelectedMemo(undefined)
    setShowProgress(true)
    MemoService.deleteMemo(accessToken, item.id as number)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_memo_remove"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          getMemos(currentSort, currentSortOrder)
        } else {
          setShowToast({ show: true, text: t("failed_memo_remove"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_memo_remove"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Box
      sx={{
        px: "20px",
        py: "40px",
      }}
    >
      <InputTextField
        name={t("memo")}
        text={memo}
        handleText={setMemo}
        multiline
        labelStyle={{ fontSize: "16px", fontWeight: "700" }}
        inputStyle={{ width: "100%" }}
        validationMessage={memoValidationMessage}
      />
      <Button
        variant="contained"
        sx={{
          minWidth: "240px",
          py: "16px",
          px: "88px",
          fontSize: "1rem",
          fontFamily: "Noto Sans JP !important",
          lineHeight: "1rem",
          fontWeight: 700,
          mt: "0px",
          mb: "40px",
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          borderRadius: 0,
          ":hover": {
            bgcolor: "#2DA389",
          },
        }}
        onClick={() => handleSave()}
      >
        {t("save")}
      </Button>
      <Typography.SubTitle
        sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mb: "20px" }}
      >
        {t("memo_history")}
      </Typography.SubTitle>
      {isTablet ? (
        <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
          {headers.map((header, index) => (
            <TableHeaderItem
              key={index}
              name={header.name}
              size={header.size}
              sortby={header.sortby}
              onSortChanged={onSortChanged}
            />
          ))}
        </Grid>
      ) : (
        <></>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isTablet
            ? { height: "auto" }
            : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        }}
      >
        {memos.length == 0 ? (
          <Typography.SubTitleNormal
            sx={{ my: "50px", mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
          >
            {t("no_data")}
          </Typography.SubTitleNormal>
        ) : (
          <>
            {memos.map((memo, index) => {
              return (
                <MemoTableItem
                  key={index}
                  item={memo}
                  handleEdit={() => setSelectedMemo(memo)}
                  handleRemove={() => handleRemove(memo)}
                />
              )
            })}

            <Box display="flex" sx={{ my: "20px", width: "100%" }}>
              <Pagination
                count={Math.ceil(total / PAGE_SIZE)}
                variant="outlined"
                color="primary"
                onChange={(event, page) => setPageNum(page)}
                sx={{ mx: "auto" }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
